import React from 'react';
import { installTiers, installAddons, installAreas, existingLockTypes } from '../../content/products';
import { serviceAreas } from '../../content/serviceAreas';
import { resolveTier, tierIncCents, travelIncCents, suburbZone, fmt, incFromEx, toCents } from '../../lib/shopPricing';

// The door check → install tier, plus the suburb that decides the travel line.
// `value` = { doorMaterial, existingLock, existingLockType, suburb, preferredTimes,
// doorNotes, hubSetup }; `product` sets the tier floor and says whether the door slides (a
// sliding-door lock is always a sliding door, so we never ask). "Not sure" prices at the
// cut-in rate — never the cheapest — and we refund the difference if the photos show a
// straight swap (Hugh, 15 Sep 2026). Only the questions that set the price are asked; swing
// direction and shelter come off the door photos after payment, and nothing here mentions
// photos (Hugh, 17 Sep: pay first, clarify after). Short labels, notes only when they
// apply, the optional fields folded away.
const Q = ({ label, children }) => (
  <div className="mb-3 sm:flex sm:items-start sm:gap-4">
    <p className="text-sm font-semibold text-gray-900 mb-1.5 sm:mb-0 sm:w-28 sm:pt-2 flex-shrink-0">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
      active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
    }`}
    aria-pressed={active}
  >
    {children}
  </button>
);

const Note = ({ tone, testId, children }) => (
  <p className={`text-sm rounded-lg border p-3 mb-3 ${tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-gray-700'}`} data-testid={testId}>
    {children}
  </p>
);

export default function InstallPicker({ product, value, onChange, count = 1, smart = false }) {
  const v = value || {};
  const hubSetup = incFromEx(toCents(installAddons.hubSetup.exGst));
  const showHub = smart || !!(product && product.ecosystems && product.ecosystems.length);
  const set = (patch) => onChange({ ...v, ...patch });
  const sliding = !!(product && product.fit && product.fit.sliding);
  const tier = resolveTier(product, { ...v, sliding });
  const zone = suburbZone(v.suburb);
  const unit = tierIncCents(tier);
  const travel = zone === 'standard' ? 0 : travelIncCents();
  const unsure = v.existingLock === 'unsure';
  const hasOptional = !!(v.preferredTimes || v.doorNotes);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <Q label="Door">
        {[
          ['timber', 'Timber'],
          ['aluminium', 'Aluminium'],
          ['other', 'Not sure'],
        ].map(([k, label]) => (
          <Chip key={k} active={v.doorMaterial === k} onClick={() => set({ doorMaterial: k })}>
            {label}
          </Chip>
        ))}
      </Q>
      <Q label="On it now">
        {[
          ['same-type', 'Same type of lock'],
          ['different', 'A different lock'],
          ['none', 'Nothing yet'],
          ['unsure', 'Not sure'],
        ].map(([k, label]) => (
          <Chip key={k} active={v.existingLock === k} onClick={() => set({ existingLock: k, existingLockType: k === 'different' ? v.existingLockType : undefined })}>
            {label}
          </Chip>
        ))}
      </Q>
      {v.existingLock === 'different' && (
        <Q label="Which?">
          {existingLockTypes.map(([k, label]) => (
            <Chip key={k} active={v.existingLockType === k} onClick={() => set({ existingLockType: k })}>
              {label}
            </Chip>
          ))}
        </Q>
      )}
      {unsure && (
        <Note tone="amber" testId="install-unsure-note">Priced at the new-install rate for now; if it turns out to be a straight swap we refund the difference before we book.</Note>
      )}
      <div className="mb-3 sm:flex sm:items-center sm:gap-4">
        <label htmlFor="install-suburb" className="text-sm font-semibold text-gray-900 block mb-1.5 sm:mb-0 sm:w-28 flex-shrink-0">
          Suburb
        </label>
        <select
          id="install-suburb"
          value={v.suburb || ''}
          onChange={(e) => set({ suburb: e.target.value })}
          className="w-full sm:max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white"
        >
          <option value="">Choose…</option>
          <optgroup label="North Shore">
            {serviceAreas.map((a) => (
              <option key={a.slug} value={a.name}>
                {a.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Central Auckland">
            {installAreas.central.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </optgroup>
          <optgroup label="Wider Auckland (travel line)">
            {installAreas.outer.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </optgroup>
          <option value="Other">Somewhere else in Auckland</option>
        </select>
      </div>
      <details className="mb-3" open={hasOptional || undefined}>
        <summary className="cursor-pointer text-sm text-gray-600 select-none">Add a preferred time or a note <span className="text-gray-400">(optional)</span></summary>
        <div className="mt-2 grid gap-2">
          <input
            id="install-times"
            type="text"
            value={v.preferredTimes || ''}
            onChange={(e) => set({ preferredTimes: e.target.value.slice(0, 120) })}
            maxLength={120}
            placeholder="When suits — e.g. weekdays after 3pm"
            aria-label="Preferred times"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-800 bg-white text-sm"
            data-testid="install-times"
          />
          <textarea
            id="install-notes"
            value={v.doorNotes || ''}
            onChange={(e) => set({ doorNotes: e.target.value.slice(0, 300) })}
            rows={2}
            maxLength={300}
            placeholder="For the installer — security door, dog, parking, gate code…"
            aria-label="Notes for the installer"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-800 bg-white text-sm"
            data-testid="install-notes"
          />
        </div>
      </details>
      {showHub && (
        <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 mb-3 cursor-pointer" data-testid="install-hub-setup">
          <input type="checkbox" checked={!!v.hubSetup} onChange={(e) => set({ hubSetup: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">
            <span className="font-semibold text-gray-900">{installAddons.hubSetup.label} · {fmt(hubSetup)}</span>
            <span className="block text-gray-600">Hub plugged in, phone paired, Apple Home / Google / Alexa and first codes set up.</span>
          </span>
        </label>
      )}
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3" data-testid="install-price">
        <p className="font-bold text-gray-900">
          {installTiers[tier].label} · {fmt(unit)} per door{count > 1 ? ` × ${count}` : ''}
          {v.hubSetup && showHub ? ` + hub setup ${fmt(hubSetup)}` : ''}
          {v.suburb && zone !== 'standard' ? ` + travel ${fmt(travel)}` : ''}
        </p>
        <p className="text-xs text-gray-600 mt-1">Incl. GST. Checked before we book — if a different lock suits your door better we say so first, and you only pay or get back the difference.</p>
      </div>
    </div>
  );
}
